import { useEffect, useState } from 'react';

import type { UserDetail } from '../../lib/panelApi';

type Props = {
  open: boolean;
  loading: boolean;
  user: UserDetail | null;
  onClose: () => void;
  onDelete: (reason: string) => Promise<void>;
};

/**
 * Right-side drawer that shows a single user's full profile,
 * library statistics, and moderation actions.
 *
 * Why a drawer and not a route:
 *   The UsersPage is paginated, and pushing a route would force the
 *   admin to keep navigating back. The drawer keeps the list state
 *   pinned and gives a faster turnaround when reviewing a batch.
 */
export function UserDetailDrawer({ open, loading, user, onClose, onDelete }: Props) {
  const [reason, setReason] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason('');
      setConfirmingDelete(false);
      setError(null);
      setDeleting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isDeleted = !!user?.deleted_at;
  const minutesListened = user
    ? Math.round(user.library_stats.total_seconds_listened / 60)
    : 0;

  const handleDelete = async () => {
    if (!user || deleting) return;
    setError(null);
    setDeleting(true);
    try {
      await onDelete(reason.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(8, 12, 28, 0.4)',
          zIndex: 40,
        }}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="User detail"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(440px, 100vw)',
          background: 'var(--panel, #fff)',
          boxShadow: '-8px 0 28px rgba(8,12,28,0.18)',
          zIndex: 50,
          overflowY: 'auto',
          padding: 18,
        }}
      >
        <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="sectionTitle" style={{ margin: 0 }}>User detail</div>
          <button className="btn" onClick={onClose} aria-label="Close">Close</button>
        </div>

        {loading || !user ? (
          <div className="muted">Loading…</div>
        ) : (
          <>
            <div className="item">
              <div style={{ fontWeight: 900, fontSize: 18 }}>
                {user.name || user.email}
                {isDeleted ? (
                  <span className="pill pill-bad" style={{ marginLeft: 8 }}>Deleted</span>
                ) : null}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                UUID: {user.uuid}
              </div>
              <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7 }}>
                <Field label="Email" value={user.email} />
                <Field label="Google ID" value={user.google_id || '—'} mono />
                <Field label="Password set" value={user.has_password ? 'Yes' : 'No'} />
                <Field label="Email verified" value={user.email_verified ? 'Yes' : 'No'} />
                <Field label="Locale" value={user.locale || '—'} />
                <Field label="Joined" value={new Date(user.created_at).toLocaleString()} />
                {user.last_login_at ? (
                  <Field label="Last login" value={new Date(user.last_login_at).toLocaleString()} />
                ) : null}
                {user.last_active_at ? (
                  <Field label="Last active" value={new Date(user.last_active_at).toLocaleString()} />
                ) : null}
                {isDeleted ? (
                  <>
                    <Field label="Deleted at" value={user.deleted_at ? new Date(user.deleted_at).toLocaleString() : ''} />
                    <Field label="Reason" value={user.deletion_reason || '—'} />
                  </>
                ) : null}
              </div>
            </div>

            <div className="item" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Library stats</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Chip label="Saved" value={user.library_stats.saved} />
                <Chip label="Listening" value={user.library_stats.listening} />
                <Chip label="Finished" value={user.library_stats.finished} />
                <Chip label="Archived" value={user.library_stats.archived} />
                <Chip label="Total min listened" value={minutesListened} />
              </div>
            </div>

            <div className="item" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Recent library</div>
              {user.recent_library.length === 0 ? (
                <div className="muted">No library entries.</div>
              ) : (
                <div className="list">
                  {user.recent_library.map((e) => (
                    <div className="item" key={e.book_uuid} style={{ paddingTop: 8, paddingBottom: 8 }}>
                      <div style={{ fontWeight: 850 }}>{e.title || 'Untitled'}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {e.status} • {Math.round(e.progress_pct * 100)}% ({Math.round(e.progress_seconds / 60)} min) • {new Date(e.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isDeleted ? (
              <div className="item" style={{ marginTop: 12, borderColor: 'var(--danger, #c33)' }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>Danger zone</div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                  Soft-deletes this user, scrambles their PII (email / name / Google ID), and lets
                  them sign up fresh later. This is irreversible.
                </div>
                {confirmingDelete ? (
                  <>
                    <div className="field">
                      <div className="label">Reason (optional)</div>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why are you deleting this account?"
                        style={{ minHeight: 60 }}
                      />
                    </div>
                    {error ? <div className="errorBox" style={{ marginBottom: 8 }}>{error}</div> : null}
                    <div className="row" style={{ gap: 8 }}>
                      <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Confirm delete'}
                      </button>
                      <button className="btn" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <button className="btn btn-danger" onClick={() => setConfirmingDelete(true)}>
                    Delete account
                  </button>
                )}
              </div>
            ) : null}
          </>
        )}
      </aside>
    </>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <span className="muted" style={{ minWidth: 110 }}>{label}</span>
      <span style={{ fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined, wordBreak: 'break-all' }}>
        {value}
      </span>
    </div>
  );
}

function Chip({ label, value }: { label: string; value: number }) {
  return (
    <div className="pill" style={{ minWidth: 90, justifyContent: 'center', flexDirection: 'column', padding: '6px 10px' }}>
      <span className="muted" style={{ fontSize: 11 }}>{label}</span>
      <span style={{ fontWeight: 900, fontSize: 16 }}>{value}</span>
    </div>
  );
}
